import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { FRAMEWORK_COLORS } from "../contexts/ComplianceContext";
import { ShieldCheck, ChevronRight, LibraryBig } from "lucide-react";
import { cn } from "../lib/utils";

export default function FrameworksPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/compliance/frameworks");
        setItems(data.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Compliance Frameworks</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Regulatory and industry frameworks tracked by CCAMS. Click any framework to view its domains, controls and current posture.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading &&
          Array.from({ length: 5 }).map((_, i) => (
            <div key={`fw-skel-${i}`} className="card-surface h-52 animate-pulse" />
          ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full py-12 text-center text-muted-foreground">
            No frameworks have been configured.
          </div>
        )}
        {items.map((f) => {
          const color = FRAMEWORK_COLORS[f.code] || "#2563EB";
          return (
            <Link
              key={f.id}
              to={`/frameworks/${f.id}`}
              data-testid={`framework-card-${f.code}`}
              className="group"
            >
              <Card className="hover:border-primary/50 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-10 h-10 rounded-md flex items-center justify-center text-white shrink-0"
                      style={{ backgroundColor: color }}
                    >
                      <ShieldCheck className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">{f.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {f.code}
                            {f.version ? ` · v${f.version}` : ""}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                      <p className="text-sm text-muted-foreground mt-3 line-clamp-2">
                        {f.description}
                      </p>

                      <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                        <Stat label="Domains" value={f.domain_count} />
                        <Stat label="Controls" value={f.controls_count} />
                        <Stat label="Complete" value={`${f.control_completion}%`} />
                      </div>

                      <div className="mt-4">
                        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                          <div
                            className={cn("h-full rounded-full")}
                            style={{
                              width: `${f.control_completion}%`,
                              backgroundColor: color,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-border py-2 px-1">
      <div className="text-sm font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
        {label}
      </div>
    </div>
  );
}
